import type { ServerError, ServerParseError } from '@apollo/client';
import type { ErrorHandler } from '@apollo/client/link/error';
import { globalHistory, navigate } from '@gatsbyjs/reach-router';
import { cond, equals, T } from 'ramda';
import Catalog from '~/Catalog';
import { ErrorTypes } from '~/Errors';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import { getErrorType } from '~/util/errorHandling';

type ReachLocationState = {
  from?: string;
};

const getRouterLocationState = (): ReachLocationState | undefined => {
  // Prefer Reach Router's own current location state
  const reachState = globalHistory.location?.state as ReachLocationState | null;
  if (reachState) return reachState;

  // Fallback: some environments store it in window.history.state.state
  const historyState = window.history.state as
    | { state?: ReachLocationState }
    | undefined
    | null;
  return historyState?.state ?? undefined;
};

/** Log out users with full page reload */
const handleNetworkError = (
  error: Error | ServerError | ServerParseError,
): void => {
  if (error.message === 'No current user') {
    window.location.replace(AUTH_PATHS.LOGOUT);
    return;
  }

  // 401 Unauthorized
  if ('statusCode' in error && error.statusCode === 401) {
    window.location.replace(AUTH_PATHS.LOGOUT);
    return;
  }

  if ('response' in error) {
    const response = (error as ServerError).response;
    if (response && 'status' in response && response.status === 401) {
      window.location.replace(AUTH_PATHS.LOGOUT);
    }
  }
};

const handleGraphQLError = (errorType: string | null): void =>
  cond([
    [equals(ErrorTypes.accountLockedError), () => void navigate('/-/locked')],
    [equals(ErrorTypes.accountPausedError), () => void navigate('/-/paused')],
    [
      equals(ErrorTypes.accountCancelledError),
      () => void navigate('/-/cancelled'),
    ],
    [
      equals(ErrorTypes.refreshRequiredError),
      () => void window.location.reload(),
    ],
    [
      equals(ErrorTypes.mfaRequiredError),
      () => {
        const state = getRouterLocationState();
        void navigate(AUTH_PATHS.MFA_SETUP_TOTP, {
          state: { from: state?.from, message: Catalog.mfaRequiredMessage },
        });
      },
    ],
    [T, () => {}],
  ])(errorType);

const errorHandler: ErrorHandler = ({ graphQLErrors, networkError }) => {
  if (networkError) {
    handleNetworkError(networkError);
    return;
  }

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    handleGraphQLError(getErrorType(graphQLErrors[0]));
  }
};

export default errorHandler;
